// components/secure/pm/ScreeningCriteriaPanel.js — Leasing → Screening criteria.
//
// Every application is judged against the published criteria in force, and the
// screening screen refuses to start without one. Until this panel the only way to
// publish (or even read) a version was scripts/seed-screening-criteria.js. Published
// versions are never edited: "New version from this one" copies the figures into a
// form, and publishing it adds a new dated version. Applications already screened
// stay bound to the version they were measured against.

import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../../hooks/useApiClient';
import { Panel, Table, Modal, Input, Select, Textarea, Button, Notice, Pill, fmtDate, titleCase } from './PMKit';

const RES = [
    ['income_multiple', 'Income multiple (× the TENANT\'S share of rent)'],
    ['min_credit_score', 'Minimum credit score'],
    ['guarantor_income_multiple', 'Guarantor income multiple'],
    ['eviction_lookback_years', 'Eviction look-back (years)'],
    ['bankruptcy_discharged_years', 'Bankruptcy discharged at least (years)'],
    ['max_late_payments_12mo', 'Max late payments in 12 months'],
    ['criminal_lookback_years', 'Criminal look-back (years) — Cook County JHA: 3 at most'],
    ['max_additional_deposit_months', 'Max additional deposit (months) on a conditional approval'],
];
const BIZ = [
    ['min_years_in_business', 'Minimum years in business'],
    ['min_paydex', 'Minimum PAYDEX'],
    ['rent_coverage_ratio', 'Rent coverage ratio'],
    ['financial_statement_years', 'Years of financial statements'],
    ['security_deposit_months', 'Security deposit (months)'],
    ['insurance_min_liability', 'Minimum liability insurance ($)'],
];
const NUMERIC = [...RES, ...BIZ].map(([k]) => k);

export default function ScreeningCriteriaPanel({ canPublish }) {
    const api = useApiClient();
    const [rows, setRows] = useState([]);
    const [form, setForm] = useState(null);
    const [view, setView] = useState(null);
    const [error, setError] = useState(null);
    const [msg, setMsg] = useState('');
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try { setRows(await api.get('/api/screening/criteria')); } catch (e) { setError({ message: e.message }); }
    }, [api]);
    useEffect(() => { load(); }, [load]);

    const fromVersion = (c) => {
        const f = { asset_class: c?.asset_class || 'residential', version: '', effective_from: new Date().toISOString().slice(0, 10),
            pm_property_id: c?.pm_property_id || '', allow_guarantor: c ? !!c.allow_guarantor : true,
            conditional_approval_allowed: c ? !!c.conditional_approval_allowed : true,
            environmental_review_required: c ? !!c.environmental_review_required : false,
            guaranty_required: c?.guaranty_required || 'case_by_case',
            applicant_notice: c?.applicant_notice || '', notes: '', rules: c?.rules || null };
        for (const k of NUMERIC) f[k] = c && c[k] != null ? String(Number(c[k])) : '';
        setForm(f);
    };

    const publish = async () => {
        if (!window.confirm(`Publish ${form.asset_class} criteria ${form.version}, effective ${form.effective_from}?\n\nA published version cannot be edited. Every application from that date is judged against it.`)) return;
        setBusy(true); setError(null); setMsg('');
        try {
            const body = { ...form, is_published: true, pm_property_id: form.pm_property_id || null };
            for (const k of NUMERIC) body[k] = form[k] === '' ? null : Number(form[k]);
            await api.post('/api/screening/criteria', body);
            setForm(null); setMsg(`Criteria ${form.version} published.`); await load();
        } catch (e) { setError({ message: e.message, problems: e.problems }); } finally { setBusy(false); }
    };

    const today = new Date().toISOString().slice(0, 10);
    const inForce = (c) => c.is_published && String(c.effective_from).slice(0, 10) <= today && (!c.retired_on || String(c.retired_on).slice(0, 10) > today);
    const fields = form ? (form.asset_class === 'residential' ? RES : BIZ) : [];

    return (
        <Panel title="Screening criteria"
            subtitle="Uniform written criteria applied to every applicant alike is the fair housing defence. Published versions are never edited — a change is a new version."
            actions={canPublish && <Button variant="ghost" onClick={() => fromVersion(null)}>+ New version</Button>}>
            {error && <Notice tone="bad" message={error.message} problems={error.problems} onDismiss={() => setError(null)} />}
            {msg && <Notice tone="good" message={msg} onDismiss={() => setMsg('')} />}
            <Table
                columns={[
                    { key: 'asset_class', label: 'Class', render: (r) => titleCase(r.asset_class) },
                    { key: 'version', label: 'Version' },
                    { key: 'pm_property_id', label: 'Applies to', render: (r) => (r.pm_property_id ? `Property #${r.pm_property_id}` : 'Firm-wide') },
                    { key: 'effective_from', label: 'Effective', nowrap: true, render: (r) => fmtDate(r.effective_from) },
                    { key: 'state', label: 'State', render: (r) => (inForce(r) ? <Pill tone="good">in force</Pill> : r.is_published ? <Pill>published</Pill> : <Pill tone="warn">draft</Pill>) },
                    { key: 'act', label: '', render: (r) => (canPublish
                        ? <button className="text-xs text-indigo-600 hover:underline" onClick={(e) => { e.stopPropagation(); fromVersion(r); }}>New version from this</button> : null) },
                ]}
                rows={rows.map((r) => ({ ...r, _key: r.criteria_id }))}
                onRowClick={(r) => setView(r)}
                empty="No criteria yet. Nobody can be screened until a version is published."
            />

            {view && (
                <Modal title={`${titleCase(view.asset_class)} criteria ${view.version}`} subtitle={`Effective ${fmtDate(view.effective_from)}${view.published_at ? ` · published ${fmtDate(view.published_at)}` : ''}`} onClose={() => setView(null)} wide>
                    <dl className="grid sm:grid-cols-2 gap-x-6 text-sm">
                        {(view.asset_class === 'residential' ? RES : BIZ).map(([k, l]) => (
                            <div key={k} className="py-1 border-b border-slate-100"><dt className="text-xs text-slate-500">{l}</dt><dd>{view[k] ?? '—'}</dd></div>
                        ))}
                    </dl>
                    {view.applicant_notice && <div className="mt-3 text-sm whitespace-pre-wrap"><div className="text-xs text-slate-500">Notice to applicants</div>{view.applicant_notice}</div>}
                </Modal>
            )}

            {form && (
                <Modal title="Publish a new criteria version" subtitle="Copied from the version you chose; change what is changing." onClose={() => setForm(null)} wide>
                    <div className="grid sm:grid-cols-3 gap-x-4">
                        <Select label="Asset class" value={form.asset_class} options={['residential', 'commercial', 'industrial']} onChange={(e) => setForm({ ...form, asset_class: e.target.value })} />
                        <Input label="Version label" placeholder="e.g. RES-2027.1" value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} />
                        <Input label="Effective from" type="date" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} />
                    </div>
                    <Input label="Only for property # (blank = firm-wide)" value={form.pm_property_id} onChange={(e) => setForm({ ...form, pm_property_id: e.target.value.replace(/\D/g, '') })}
                        hint="A property override is an exception an owner asked for, with a name on it." />
                    <div className="grid sm:grid-cols-2 gap-x-4">
                        {fields.map(([k, l]) => <Input key={k} label={l} type="number" step="any" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />)}
                    </div>
                    {form.asset_class === 'residential' ? (
                        <div className="flex flex-wrap gap-4 text-sm mb-3">
                            <label className="flex items-center gap-2"><input type="checkbox" checked={form.allow_guarantor} onChange={(e) => setForm({ ...form, allow_guarantor: e.target.checked })} /> Guarantor accepted</label>
                            <label className="flex items-center gap-2"><input type="checkbox" checked={form.conditional_approval_allowed} onChange={(e) => setForm({ ...form, conditional_approval_allowed: e.target.checked })} /> Conditional approval allowed</label>
                        </div>
                    ) : (
                        <div className="grid sm:grid-cols-2 gap-x-4">
                            <Select label="Personal guaranty" value={form.guaranty_required} options={['case_by_case', 'personal', 'corporate', 'letter_of_credit', 'none']} onChange={(e) => setForm({ ...form, guaranty_required: e.target.value })} />
                            <label className="flex items-center gap-2 text-sm mt-5"><input type="checkbox" checked={form.environmental_review_required} onChange={(e) => setForm({ ...form, environmental_review_required: e.target.checked })} /> Environmental review required</label>
                        </div>
                    )}
                    <Textarea label="Notice to applicants (published wording)" rows={5} value={form.applicant_notice} onChange={(e) => setForm({ ...form, applicant_notice: e.target.value })} />
                    <Textarea label="Internal notes (why this version)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                    <div className="flex justify-end gap-2">
                        <Button variant="ghost" onClick={() => setForm(null)}>Cancel</Button>
                        <Button disabled={busy || !form.version || !form.effective_from} onClick={publish}>Publish</Button>
                    </div>
                </Modal>
            )}
        </Panel>
    );
}
